// SSRF-/DoS-Härtung für adapter-seitige Fetches (D56/D58-Geist). Gespiegelt aus dem
// `RunnerEnv.fetchBytes`-Guard (apps/api-worker/src/consumer.ts) — das `providers`-Paket
// darf NICHT aus `apps` importieren (Abhängigkeitsrichtung), daher hier eng dupliziert.
// Regeln: nur https, KEINE Redirects (Redirect-zu-intern-Vektor), interne/private Hosts
// geblockt, Größenlimit; optional content-type-Präfix-Check. Die volle provider-bezogene
// Host-ALLOWLIST folgt mit den echten Provider-Domains (§13/M6).

const PRIVATE_V4 = /^(0|10|127|169\.254|192\.168|172\.(1[6-9]|2\d|3[01]))\./;

/** Wirft, wenn die URL kein öffentliches https-Ziel ist (SSRF-Defense-in-Depth). Gibt die URL zurück. */
export function assertPublicHttpsUrl(url: string): URL {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    throw new Error("safe-fetch: ungültige URL");
  }
  if (parsed.protocol !== "https:") throw new Error("safe-fetch: nur https-URLs erlaubt");
  // Trailing-Dot (FQDN-Root) abschneiden, sonst umgeht `localhost.` / `x.internal.` den Check.
  const host = parsed.hostname.toLowerCase().replace(/\.+$/, "");
  if (
    host === "localhost" ||
    host.endsWith(".internal") ||
    host.endsWith(".local") ||
    host.includes(":") || // IPv6-Literal (inkl. ::1) — konservativ blocken
    PRIVATE_V4.test(host)
  )
    throw new Error("safe-fetch: interner/privater Host blockiert");
  return parsed;
}

export interface SafeFetchOptions {
  signal?: AbortSignal;
  /** Größenlimit in Bytes (Default 32 MiB — ein Eingabe-Bild). */
  maxBytes?: number;
  /** Erwarteter content-type-Präfix (z.B. "image/") — bei Abweichung Fehler. */
  expectContentType?: string;
}

const DEFAULT_MAX_BYTES = 32 * 1024 * 1024;

/**
 * https-only, kein Redirect, Größenlimit, optional content-type-Präfix-Check. Liefert die
 * Bytes + den normalisierten content-type (leer ⇒ Fallback "application/octet-stream").
 */
export async function safeFetchBytes(
  url: string,
  opts: SafeFetchOptions = {},
): Promise<{ bytes: Uint8Array; contentType: string }> {
  assertPublicHttpsUrl(url);
  const maxBytes = opts.maxBytes ?? DEFAULT_MAX_BYTES;
  // redirect:"manual" (Cloudflare Workers unterstützen "error" NICHT) + jeden 3xx als
  // blockierten Redirect behandeln (SSRF: Redirect-zu-intern-Vektor bleibt geschlossen).
  const res = await fetch(url, { signal: opts.signal, redirect: "manual" });
  if (res.status >= 300 && res.status < 400) throw new Error("safe-fetch: Redirect blockiert (SSRF-Defense)");
  if (!res.ok) {
    // Status STRUKTURIERT mitgeben (rückwärtskompatibel — die Meldung bleibt gleich), damit
    // Aufrufer transiente (429/5xx) von terminalen (4xx) Download-Fehlern unterscheiden können.
    const err = new Error(`safe-fetch: HTTP ${res.status} ${res.statusText}`) as Error & { status?: number };
    err.status = res.status;
    throw err;
  }
  const contentType = (res.headers.get("content-type") ?? "").split(";")[0]!.trim().toLowerCase();
  if (opts.expectContentType && !contentType.startsWith(opts.expectContentType))
    throw new Error(`safe-fetch: unerwarteter content-type '${contentType || "—"}' (erwartet ${opts.expectContentType}*)`);
  const declared = Number(res.headers.get("content-length") ?? "0");
  if (declared > maxBytes) throw new Error("safe-fetch: über Größenlimit");
  const buf = await res.arrayBuffer();
  if (buf.byteLength > maxBytes) throw new Error("safe-fetch: über Größenlimit");
  return { bytes: new Uint8Array(buf), contentType: contentType || "application/octet-stream" };
}
